import { useMemo } from 'react';
import { Activity, AlertTriangle, ArrowRight, Brain, CheckCircle2, Database, KeyRound, LockKeyhole, Plug, ShieldCheck, Settings2, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenant } from '@/hooks/useTenant';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { useConnectedSources } from '@/hooks/useConnectedSources';
import { useAttention } from '@/hooks/useAttention';
import { cn } from '@/lib/utils';

interface ControlCenterProps {
  onNavigate: (view: string) => void;
}

const roleLabel: Record<string, string> = {
  admin: 'Admin',
  pmo: 'PMO',
  executive: 'Executive',
  program_manager: 'Program Manager',
  senior_project_manager: 'Senior PM',
  project_manager: 'Project Manager',
  user: 'User',
};

const sourceTone = {
  ok: { label: 'Healthy', icon: CheckCircle2, className: 'text-success' },
  warn: { label: 'Needs attention', icon: AlertTriangle, className: 'text-warning' },
  off: { label: 'Paused', icon: Activity, className: 'text-muted-foreground' },
};

const ControlCenter = ({ onNavigate }: ControlCenterProps) => {
  const { tenantName, tenantSlug, isDemoTenant } = useTenant();
  const { members, loading: membersLoading } = useTenantMembers();
  const { settings, isLoading: settingsLoading } = useTenantSettings();
  const { data: sources = [], isLoading: sourcesLoading } = useConnectedSources();
  const attention = useAttention();

  const roleCounts = useMemo(() => members.reduce<Record<string, number>>((acc, member) => {
    acc[member.role] = (acc[member.role] ?? 0) + 1;
    return acc;
  }, {}), [members]);

  const connected = sources.filter((s) => s.status === 'ok').length;
  const needsAttention = sources.filter((s) => s.status === 'warn').length;
  const activeConnections = sources.filter((s) => s.status !== 'off').length;

  const settingsReady = !settingsLoading;
  const securityChecks = [
    { label: 'Human approval', good: settings.aiHumanApprovalRequired },
    { label: 'MFA enforcement', good: settings.mfaRequired },
    { label: 'SSO', good: Boolean(settings.ssoConfig.provider) },
    { label: 'IP restriction', good: true },
  ];
  const postureCount = securityChecks.filter((x) => x.good).length;
  const loading = membersLoading || sourcesLoading || !settingsReady;

  const go = (view: string) => onNavigate(view);
  const integrationsPath = tenantSlug ? '/' + tenantSlug + '/integrations' : '/integrations';

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between'>
        <div>
          <p className='font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground'>Admin Control Center</p>
          <div className='mt-1 flex flex-wrap items-center gap-2'>
            <h2 className='text-2xl font-bold text-foreground'>{tenantName || 'Organization'}</h2>
            {isDemoTenant && <Badge variant='outline'>Demo</Badge>}
          </div>
          <p className='mt-1 max-w-3xl text-sm text-muted-foreground'>
            Govern the Masira environment. Manage people, connections, AI behavior, security and evidence from one place.
          </p>
        </div>
        <div className='flex flex-wrap gap-2'>
          <Button variant='outline' onClick={() => go('tenantSettings')}>Organization settings</Button>
          <Button onClick={() => go('auditTrail')}>Open audit trail</Button>
        </div>
      </div>

      <div className='grid grid-cols-2 gap-3 xl:grid-cols-4'>
        <Card><CardContent className='p-4'>
          <div className='flex items-center justify-between'><p className='text-xs text-muted-foreground'>People</p><Users className='h-4 w-4 text-muted-foreground' /></div>
          {loading ? <Skeleton className='mt-2 h-8 w-16' /> : <p className='mt-2 text-3xl font-bold'>{members.length}</p>}
          <p className='mt-1 text-xs text-muted-foreground'>{roleCounts.admin ?? 0} admins · {roleCounts.pmo ?? 0} PMO</p>
        </CardContent></Card>

        <Card><CardContent className='p-4'>
          <div className='flex items-center justify-between'><p className='text-xs text-muted-foreground'>Connections</p><Plug className='h-4 w-4 text-muted-foreground' /></div>
          {loading ? <Skeleton className='mt-2 h-8 w-16' /> : <p className='mt-2 text-3xl font-bold'>{connected}</p>}
          <p className='mt-1 text-xs text-muted-foreground'>{needsAttention > 0 ? needsAttention + ' need attention' : activeConnections ? 'All active connections healthy' : 'No active connections'}</p>
        </CardContent></Card>

        <Card><CardContent className='p-4'>
          <div className='flex items-center justify-between'><p className='text-xs text-muted-foreground'>Governance</p><ShieldCheck className='h-4 w-4 text-muted-foreground' /></div>
          <p className='mt-2 text-3xl font-bold'>{attention.pendingApprovals + attention.pendingDecisions}</p>
          <p className='mt-1 text-xs text-muted-foreground'>{attention.pendingApprovals} approvals · {attention.pendingDecisions} decisions waiting</p>
        </CardContent></Card>

        <Card><CardContent className='p-4'>
          <div className='flex items-center justify-between'><p className='text-xs text-muted-foreground'>Security posture</p><LockKeyhole className='h-4 w-4 text-muted-foreground' /></div>
          <p className='mt-2 text-3xl font-bold'>{postureCount}/4</p>
          <p className='mt-1 text-xs text-muted-foreground'>control areas configured</p>
        </CardContent></Card>
      </div>

      <div className='grid gap-6 xl:grid-cols-2'>
        <Card>
          <CardHeader className='pb-3'>
            <div className='flex items-start justify-between gap-3'>
              <div><CardTitle className='flex items-center gap-2'><Users className='h-5 w-5 text-primary' />People & access</CardTitle><CardDescription>Who can enter Masira and what role they hold.</CardDescription></div>
              <Button variant='ghost' size='sm' onClick={() => go('tenantSettings')}>Manage <ArrowRight className='ms-1 h-4 w-4' /></Button>
            </div>
          </CardHeader>
          <CardContent className='space-y-3'>
            {membersLoading ? [...Array(4)].map((_, index) => <Skeleton key={index} className='h-12 w-full rounded-lg' />) : members.length === 0 ? (
              <div className='rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground'>No members found.</div>
            ) : members.slice(0, 5).map((member) => (
              <div key={member.user_id} className='flex items-center gap-3 rounded-lg border p-3'>
                <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary'>{(member.display_name || member.email || '?').slice(0, 1).toUpperCase()}</div>
                <div className='min-w-0 flex-1'><p className='truncate text-sm font-medium'>{member.display_name || member.email}</p><p className='truncate text-xs text-muted-foreground'>{member.email}</p></div>
                <Badge variant='outline'>{roleLabel[member.role] ?? member.role}</Badge>
              </div>
            ))}
            {!membersLoading && members.length > 5 && <p className='pt-1 text-xs text-muted-foreground'>+{members.length - 5} more members</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='pb-3'>
            <div className='flex items-start justify-between gap-3'>
              <div><CardTitle className='flex items-center gap-2'><Database className='h-5 w-5 text-primary' />Connected systems</CardTitle><CardDescription>Masira reads from your existing enterprise systems.</CardDescription></div>
              <Button variant='ghost' size='sm' onClick={() => window.location.assign(integrationsPath)}>Manage <ArrowRight className='ms-1 h-4 w-4' /></Button>
            </div>
          </CardHeader>
          <CardContent className='space-y-3'>
            {sourcesLoading ? [...Array(3)].map((_, index) => <Skeleton key={index} className='h-14 w-full rounded-lg' />) : sources.length === 0 ? (
              <div className='rounded-lg border border-dashed p-6 text-center'><Plug className='mx-auto h-8 w-8 text-muted-foreground' /><p className='mt-2 text-sm font-medium text-foreground'>No systems connected</p><p className='mt-1 text-xs text-muted-foreground'>Connect Jira, Microsoft 365, Azure DevOps and other approved sources.</p><Button size='sm' className='mt-3' onClick={() => window.location.assign(integrationsPath)}>Connect a system</Button></div>
            ) : sources.slice(0, 6).map((source) => {
              const tone = sourceTone[source.status];
              const Icon = tone.icon;
              return <div key={source.id} className='flex items-center gap-3 rounded-lg border p-3'><div className='rounded-lg bg-muted p-2'><Plug className='h-4 w-4 text-muted-foreground' /></div><div className='min-w-0 flex-1'><p className='truncate text-sm font-medium'>{source.name}</p><p className='text-xs text-muted-foreground'>{source.lastSyncAt ? 'Last sync ' + new Date(source.lastSyncAt).toLocaleString() : 'No successful sync yet'}</p></div><div className={cn('flex items-center gap-1 text-xs font-medium', tone.className)}><Icon className='h-3.5 w-3.5' />{tone.label}</div></div>;
            })}
          </CardContent>
        </Card>
      </div>

      <div className='grid gap-6 xl:grid-cols-3'>
        <Card><CardHeader className='pb-3'><CardTitle className='flex items-center gap-2 text-base'><ShieldCheck className='h-4 w-4 text-primary' />Governance posture</CardTitle><CardDescription>Rules that shape how Masira is used.</CardDescription></CardHeader>
          <CardContent className='space-y-2'>
            {[['AI human approval', settings.aiHumanApprovalRequired], ['Evidence-based risk signals', settings.riskSignalsEvidenceBased], ['No people analytics', settings.riskSignalsNoPeopleAnalytics], ['No HR/performance usage', settings.smartInboxNoHrUsage]].map(([label, enabled]) => <div key={String(label)} className='flex items-center justify-between rounded-md border px-3 py-2.5'><span className='text-sm'>{String(label)}</span><Badge variant='outline' className={cn(enabled ? 'border-success/30 text-success' : 'border-warning/30 text-warning')}>{enabled ? 'Enabled' : 'Review'}</Badge></div>)}
            <Button variant='ghost' size='sm' className='mt-2 w-full' onClick={() => go('tenantSettings')}>Review governance <ArrowRight className='ms-1 h-4 w-4' /></Button>
          </CardContent>
        </Card>

        <Card><CardHeader className='pb-3'><CardTitle className='flex items-center gap-2 text-base'><Brain className='h-4 w-4 text-primary' />AI controls</CardTitle><CardDescription>Capabilities, trust controls and model governance.</CardDescription></CardHeader>
          <CardContent className='space-y-3'>
            <div className='rounded-lg border p-3'><div className='flex items-center justify-between'><span className='text-sm font-medium'>Human approval required</span><Badge variant='outline' className={settings.aiHumanApprovalRequired ? 'border-success/30 text-success' : 'border-destructive/30 text-destructive'}>{settings.aiHumanApprovalRequired ? 'Enforced' : 'Disabled'}</Badge></div><p className='mt-2 text-xs text-muted-foreground'>AI prepares. Humans approve, decide and remain accountable.</p></div>
            <div className='grid grid-cols-2 gap-2'><Button variant='outline' onClick={() => go('aiControls')}>AI controls</Button><Button variant='ghost' onClick={() => go('auditTrail')}>AI activity</Button></div>
          </CardContent>
        </Card>

        <Card><CardHeader className='pb-3'><CardTitle className='flex items-center gap-2 text-base'><KeyRound className='h-4 w-4 text-primary' />Security</CardTitle><CardDescription>Identity and access protection.</CardDescription></CardHeader>
          <CardContent className='space-y-2'>
            {[['MFA', settings.mfaRequired ? 'Required' : 'Not required', settings.mfaRequired], ['SSO', settings.ssoConfig.provider ? settings.ssoConfig.provider : 'Not configured', Boolean(settings.ssoConfig.provider)], ['IP allowlist', settings.ipAllowlist.length ? String(settings.ipAllowlist.length) + ' ranges' : 'Open', true]].map(([label, value, good]) => <div key={String(label)} className='flex items-center justify-between rounded-md border px-3 py-2.5'><span className='text-sm'>{String(label)}</span><span className={cn('text-xs font-medium', good ? 'text-success' : 'text-warning')}>{String(value)}</span></div>)}
            <Button variant='ghost' size='sm' className='mt-2 w-full' onClick={() => go('tenantSettings')}>Security settings <ArrowRight className='ms-1 h-4 w-4' /></Button>
          </CardContent>
        </Card>
      </div>

      <Card className='border-primary/20 bg-primary/[0.03]'>
        <CardHeader className='pb-3'><CardTitle className='flex items-center gap-2'><Settings2 className='h-5 w-5 text-primary' />Control shortcuts</CardTitle><CardDescription>Common admin tasks without digging through settings.</CardDescription></CardHeader>
        <CardContent className='grid gap-3 sm:grid-cols-2 xl:grid-cols-4'>
          {[{ label: 'Manage people', description: 'Roles, invitations and access', view: 'tenantSettings', icon: Users }, { label: 'Review AI controls', description: 'Capabilities and approval rules', view: 'aiControls', icon: Brain }, { label: 'Inspect audit trail', description: 'Decisions, evidence and changes', view: 'auditTrail', icon: ShieldCheck }, { label: 'Manage organisation', description: 'SSO, security and org policy', view: 'tenantSettings', icon: Settings2 }].map((item) => {
            const Icon = item.icon;
            return <button key={item.label} type='button' onClick={() => go(item.view)} className='rounded-lg border p-4 text-start transition-colors hover:bg-muted/30'><Icon className='h-5 w-5 text-primary' /><p className='mt-3 text-sm font-semibold'>{item.label}</p><p className='mt-1 text-xs text-muted-foreground'>{item.description}</p></button>;
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default ControlCenter;